import { ApplicationConfig, ErrorHandler, provideZoneChangeDetection, importProvidersFrom, isDevMode } from '@angular/core';
import { provideServiceWorker } from '@angular/service-worker';
import { AppUpdateService } from './core/services/app-update.service';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withFetch, withInterceptors, HTTP_INTERCEPTORS, withInterceptorsFromDi } from '@angular/common/http';
import { authInterceptor } from './interceptors/auth.interceptor';
import { sesionTerminadaInterceptor } from './core/auth/sesion-terminada.interceptor';
import { sesionTerminadaRechazoInterceptor } from './core/auth/sesion-terminada-rechazo.interceptor';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { DateAdapter, provideNativeDateAdapter } from '@angular/material/core';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { MatSortHeaderIntl } from '@angular/material/sort';
import { LocalizedPaginatorIntl } from './shared/services/localized-paginator-intl.service';
import { LocalizedDateAdapter } from './shared/services/localized-date-adapter.service';
import { LocalizedSortIntl } from './shared/services/localized-sort-intl.service';
import { FormBuilder } from '@angular/forms';
import { VerifyAuthCodeService } from './services/verify-auth-code.service';
import { BrowserStorageService } from './services/browser-storage.service';
import { APP_INITIALIZER } from '@angular/core';
import { AppInitializerService } from './services/app-initializer.service';
import { ThemeService } from './core/theme/theme.service';
import { TenantThemeService } from './core/theme/tenant-theme.service';
import { MatDialogModule } from '@angular/material/dialog';
import { provideTransloco } from '@jsverse/transloco';
import { TranslocoHttpLoader } from './core/i18n/transloco-loader';
import { LanguageService } from './core/i18n/language.service';
import { UserAccessRepository } from './domain/ports/user-access.repository';
import { UserAccessRemoteDataSource } from './data/data-sources/remote/user-access-remote.data-source';
import { ApiKeyInterceptor } from './interceptors/api-key.interceptor';
import { TotpLockInterceptor } from './core/http/totp-lock.interceptor';
import { RequestTimeoutInterceptor } from './core/http/request-timeout.interceptor';
import { StorageEncryptor } from './domain/ports/storage-encryptor.port';
import { LegacyStorageEncryptorAdapter } from './data/adapters/legacy-storage-encryptor.adapter';
import { StoreUsersRepository } from './domain/ports/store-users.repository';
import { StoreUsersRemoteDataSource } from './data/data-sources/remote/store-users-remote.data-source';
import { TPV_REST_PROVIDERS } from './data/tpv/tpv.providers';
import { GLOBAL_DASHBOARD_PROVIDERS } from './data/global-dashboard.providers';
import { PAYMENTS_DASHBOARD_PROVIDERS } from './data/payments-dashboard/payments-dashboard.providers';
import { CountryRepository } from './domain/ports/country.repository';
import { CountryRemoteDataSource } from './data/data-sources/remote/country-remote.data-source';
import { CountryMockDataSource } from './data/data-sources/mock/country-mock.data-source';
import { PolipayPaymentsRepository } from './domain/ports/polipay-payments.repository';
import { PolipayPaymentsRemoteDataSource } from './data/data-sources/remote/polipay-payments-remote.data-source';
import { PolipayPaymentsMockDataSource } from './data/data-sources/mock/polipay-payments-mock.data-source';
import { GlobalErrorHandler } from './core/errors/global-error-handler';
import { BrowserPage, WindowBrowserPage } from './core/errors/browser-page';
import { providePaymentQrRepository } from './data/payments-qr/payment-qr.providers';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideCardsSpeiAttemptMemory } from './core/services/cards-spei-attempt-memory.service';
import { provideSpeiOrderAttemptMemory } from './core/services/spei-order-attempt-memory.service';

export function appInitializerFactory(initializerService: AppInitializerService) {
 return () => initializerService.loadAuthData();
}

export function themeInitializerFactory(themeService: ThemeService) {
 return () => themeService.loadCachedTheme();
}

export function tenantThemeInitializerFactory(tenantThemeService: TenantThemeService) {
 return () => tenantThemeService.loadTenantTheme();
}

export function languageInitializerFactory(languageService: LanguageService) {
 return () => languageService.init();
}

export const appConfig: ApplicationConfig = {
 providers: [
  // Motor de animaciones de Angular. Hace falta para la SALIDA de los paneles
  // desplegables: con `@if` el elemento se va del DOM en el acto y una
  // `animation` de CSS solo puede correr al entrar. Es ADITIVO — hasta acá
  // ningún componente declaraba `animations: []` y Material v19 anima por CSS,
  // así que no cambia el comportamiento de nada que ya existiera.
  provideAnimations(),
  provideZoneChangeDetection({ eventCoalescing: true }), 
  provideRouter(routes), 
  // Manejador de errores global: registra todo y recupera la pestaña que quedó
  // con el index.html de un build anterior (ChunkLoadError tras un despliegue).
  { provide: ErrorHandler, useClass: GlobalErrorHandler },
  { provide: BrowserPage, useClass: WindowBrowserPage },
  provideNativeDateAdapter(),
  { provide: DateAdapter, useClass: LocalizedDateAdapter },
  importProvidersFrom(MatDialogModule), 
  // 🔑 CLAVE: Añadimos withInterceptorsFromDi() para que funcione la inyección de la clase PreviousSessionInterceptor
  provideHttpClient(
   withFetch(),
   // Va ANTES de los de clase: con la sesión terminada corta la petición sin que
   // llegue a `ApiKeyInterceptor` ni a `authInterceptor` (feature 33).
   withInterceptors([sesionTerminadaInterceptor]),
      withInterceptorsFromDi(),
   // Va DESPUÉS de los de clase, o sea POR DENTRO del `authInterceptor`: ve el
   // rechazo tardío antes que su `catchError` y lo absorbe (feature 33).
   withInterceptors([sesionTerminadaRechazoInterceptor]),
  ),
  // Este registro de clase ahora funcionará
  {
   provide: HTTP_INTERCEPTORS,
   useClass: ApiKeyInterceptor,
   multi: true
  },
  {
   provide: HTTP_INTERCEPTORS,
   useClass: authInterceptor,
   multi: true
  },
  // Registrado DESPUÉS de `authInterceptor` ⇒ más cerca del backend: ve el 429
  // `TOTP_LOCKED` antes que nadie, lo anuncia y lo repropaga intacto.
  {
   provide: HTTP_INTERCEPTORS,
   useClass: TotpLockInterceptor,
   multi: true
  },
  // El ÚLTIMO (el más cercano al backend) a propósito: el tope de
  // `REQUEST_ATTEMPT_TIMEOUT_MS` cuenta CADA intento contra el servidor —el
  // original y el reintento tras renovar— y deja fuera la espera del diálogo de
  // sesión, que vive arriba, en `authInterceptor`. Un `timeout()` por encima de
  // la cadena vencería con el diálogo abierto y colgaría el refresh de la app.
  {
   provide: HTTP_INTERCEPTORS,
   useClass: RequestTimeoutInterceptor,
   multi: true
  },
  AppInitializerService,
  {
   provide: APP_INITIALIZER,
   useFactory: appInitializerFactory,
   deps: [AppInitializerService],
   multi: true
  },
  {
   provide: APP_INITIALIZER,
   useFactory: themeInitializerFactory,
   deps: [ThemeService],
   multi: true
  },
  {
   provide: APP_INITIALIZER,
   useFactory: tenantThemeInitializerFactory,
   deps: [TenantThemeService],
   multi: true
  },
  { provide: UserAccessRepository, useClass: UserAccessRemoteDataSource },
  { provide: StoreUsersRepository, useClass: StoreUsersRemoteDataSource },
  {
   provide: CountryRepository,
   useClass: environment.countryUseMocks === true ? CountryMockDataSource : CountryRemoteDataSource,
  },
  // Polipay Payments (api/v1/payments) — toggle mock/remote por environment.useMocks
  {
   provide: PolipayPaymentsRepository,
   useClass: environment.useMocks === true ? PolipayPaymentsMockDataSource : PolipayPaymentsRemoteDataSource,
  },
  // Cobro QR (api/v1/payments/qr) — port propio, mismo toggle. Es root porque la
  // sonda de disponibilidad la comparte el menú lateral.
  providePaymentQrRepository(environment.useMocks === true),
  { provide: StorageEncryptor, useClass: LegacyStorageEncryptorAdapter },
  // Intentos de orden SPEI de Cards por nodo (feature 124): memoria de la PESTAÑA.
  // ⚠️ Solo aquí: proveerla en una página la re-crearía y estrenaría llave al volver.
  ...provideCardsSpeiAttemptMemory(),
  // Intentos de orden SPEI de Suite por origen (feature 125): la misma memoria de pestaña.
  // ⚠️ Solo aquí, por la misma razón.
  ...provideSpeiOrderAttemptMemory(),
  // TPV REST (api/v1/tpv) — toggle mock/remote por environment.tpvUseMocks
  ...TPV_REST_PROVIDERS,
  // Dashboard Global "Todo" — toggle mock/remote por environment.globalDashboardUseMocks
  ...GLOBAL_DASHBOARD_PROVIDERS,
  // Dashboard de la Pasarela de Pagos — root porque "Todo" también lo consume.
  // Toggle mock/remote por environment.paymentsDashboardUseMocks.
  ...PAYMENTS_DASHBOARD_PROVIDERS,
  FormBuilder,
  VerifyAuthCodeService,
  BrowserStorageService,
  { provide: MatPaginatorIntl, useClass: LocalizedPaginatorIntl },
  { provide: MatSortHeaderIntl, useClass: LocalizedSortIntl },
  provideTransloco({
   config: {
    availableLangs: ['es', 'en', 'pt'],
    defaultLang: 'es',
    fallbackLang: 'es',
    reRenderOnLangChange: true,
    prodMode: !isDevMode(),
    missingHandler: {
     useFallbackTranslation: true,
     logMissingKey: true,
    },
   },
   loader: TranslocoHttpLoader,
  }),
  {
   provide: APP_INITIALIZER,
   useFactory: languageInitializerFactory,
   deps: [LanguageService],
   multi: true
  },
  provideServiceWorker('ngsw-worker.js', {
   enabled: !isDevMode(),
   registrationStrategy: 'registerWhenStable:30000'
  }),
  {
   provide: APP_INITIALIZER,
   useFactory: (updateService: AppUpdateService) => () => updateService.init(),
   deps: [AppUpdateService],
   multi: true
  },
 ]
};